<template>
  <div class="chart-holder">
    <app-spinner v-if="!isLoaded"/>
    <template v-else>
      <div v-for="chart in normalizedCharts" :key="chart.chart_key">
        <app-cells>
          <h2 class="title dashboard-chart-title">{{ chart.title }}</h2>
          <button
            v-if="hasChartInfo(chart)"
            class="dashboard-info-btn dashboard-info-btn--small"
            type="button"
            @click="openChartInfo(chart)"
          >
            i
          </button>
        </app-cells>
        <div class="charts-grid">
          <highcharts :options="chart.options"/>
        </div>
      </div>
    </template>
    <modal
      :adaptive="true"
      :height="'auto'"
      :scrollable="true"
      :width="720"
      class="modal-not-overflow"
      name="marketing-dashboard-info-modal"
    >
      <div class="popup marketing-dashboard-info-modal">
        <button class="popup__close" type="button" @click="closeInfoModal"></button>
        <div class="popup__title">{{ info_modal.title }}</div>
        <div class="popup__body marketing-dashboard-info-modal__body">
          <template v-if="info_modal.sections.length">
            <div
              v-for="section in info_modal.sections"
              :key="section.key"
              class="marketing-dashboard-info-section"
            >
              <h3 v-if="section.title" class="marketing-dashboard-info-section__title">
                {{ section.title }}
              </h3>
              <div
                class="marketing-dashboard-info-section__content"
                v-html="renderMarkdown(section.markdown)"
              ></div>
            </div>
          </template>
          <p v-else class="marketing-dashboard-info-modal__empty">No info available</p>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'MarketingDashboardCharts',
  components: {highcharts: Chart},
  props: {
    charts: {
      type: Array,
      default: () => [],
    },
    dashboardInfo: {
      type: Object,
      default: () => ({}),
    },
    isLoaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      info_modal: {
        title: '',
        sections: [],
      },
    }
  },
  computed: {
    normalizedCharts() {
      return (this.charts || []).map(chart => ({
        ...chart,
        info: chart.info || this.getChartInfoFromDashboardInfo(chart.chart_key),
        options: this.getChartOptions(chart),
      }))
    },
  },
  methods: {
    getChartOptions(chart) {
      const vm = this
      const yAxisByKey = this.getYAxisByKey(chart)
      const options = {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: chart.chart_type === 'distribution' ? 'column' : 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: chart.categories,
        },
        yAxis: this.getChartYAxisOptions(chart),
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: (chart.series || []).map(series => this.getSeriesOptions(series, yAxisByKey))
      }

      if (this.hasChartValueMeta(chart)) {
        options.plotOptions.series.dataLabels.formatter = function () {
          const meta = vm.getSeriesValueMeta(chart, this.series.options)

          return vm.formatChartValue(this.y, meta)
        }
        options.tooltip = {
          pointFormatter() {
            const meta = vm.getSeriesValueMeta(chart, this.series.options)
            const color = this.color || this.series.color

            return `<span style="color:${color}">\u25CF</span> ${this.series.name}: <b>${vm.formatChartValue(this.y, meta)}</b><br/>`
          }
        }
      }

      return options
    },
    getChartYAxisOptions(chart) {
      const yAxes = this.getChartYAxes(chart)

      if (!yAxes.length) {
        const yAxis = {
          title: {
            text: ''
          }
        }
        const yAxisMeta = this.getYAxisValueMeta(chart)

        if (this.hasValueMeta(yAxisMeta)) {
          yAxis.labels = this.getYAxisLabelsOptions(yAxisMeta)
        }

        return yAxis
      }

      return yAxes.map(axis => {
        const axisOptions = {
          id: this.getYAxisKey(axis),
          title: {
            text: axis.title || ''
          },
          opposite: axis.opposite === true,
        }

        if (this.hasValueMeta(axis.meta)) {
          axisOptions.labels = this.getYAxisLabelsOptions(axis.meta)
        }

        return axisOptions
      })
    },
    getYAxisLabelsOptions(meta) {
      const vm = this

      return {
        formatter() {
          return vm.formatChartValue(this.value, {
            ...meta,
            axis: true,
          })
        }
      }
    },
    getSeriesOptions(series, yAxisByKey) {
      const seriesOptions = {
        ...series,
        type: series.type,
        visible: series.visible !== false,
      }

      if (series.y_axis_key && yAxisByKey[series.y_axis_key]) {
        seriesOptions.yAxis = series.y_axis_key
      }

      return seriesOptions
    },
    getChartYAxes(chart) {
      return Array.isArray(chart.y_axes) ? chart.y_axes : []
    },
    getYAxisByKey(chart) {
      return this.getChartYAxes(chart).reduce((result, axis) => {
        const key = this.getYAxisKey(axis)

        if (key) result[key] = axis

        return result
      }, {})
    },
    getYAxisKey(axis) {
      return axis.key || axis.id
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    hasChartValueMeta(chart) {
      const yAxisByKey = this.getYAxisByKey(chart)

      return this.hasValueMeta(chart.meta) || (chart.series || []).some(series => {
        const axis = series.y_axis_key ? yAxisByKey[series.y_axis_key] : null

        return this.hasValueMeta(series.meta) || this.hasValueMeta(axis && axis.meta)
      })
    },
    hasValueMeta(meta) {
      return Boolean(meta && (meta.unit || meta.value_format || meta.precision === 0 || meta.precision))
    },
    getSeriesValueMeta(chart, series) {
      if (series && series.meta) return series.meta

      const yAxisByKey = this.getYAxisByKey(chart)
      const axis = series && series.y_axis_key ? yAxisByKey[series.y_axis_key] : null

      return (axis && axis.meta) || chart.meta
    },
    getYAxisValueMeta(chart) {
      return this.hasValueMeta(chart.meta) ? chart.meta : null
    },
    formatChartValue(value, meta) {
      const number = Number(value)

      if (!meta || Number.isNaN(number)) return value

      let formattedValue = this.formatNumber(number, this.getNumberPrecision(meta))

      if (meta.value_format === 'compact') formattedValue = this.formatCompactNumber(number, meta)

      if (meta.unit === 'usd') return number < 0 ? `-$${formattedValue.replace('-', '')}` : `$${formattedValue}`
      if (meta.unit === 'percent') return `${formattedValue}%`

      return formattedValue
    },
    formatCompactNumber(value, meta) {
      const absValue = Math.abs(value)
      const precision = this.getCompactPrecision(meta)
      const compact_formats = [
        {value: 1000000000, suffix: 'B'},
        {value: 1000000, suffix: 'M'},
        {value: 1000, suffix: 'K'},
      ]
      const compact_format = compact_formats.find(format => absValue >= format.value)

      if (!compact_format) {
        const precision = meta.axis ? null : this.getNumberPrecision(meta)

        return this.formatNumber(value, precision)
      }

      const sign = value < 0 ? '-' : ''
      const formattedValue = this.formatNumber(absValue / compact_format.value, precision)

      return `${sign}${formattedValue}${compact_format.suffix}`
    },
    getCompactPrecision(meta) {
      if (meta.axis) return null

      const precision = this.getMetaPrecision(meta)

      return precision === null ? 1 : precision
    },
    getNumberPrecision(meta) {
      const precision = this.getMetaPrecision(meta)

      if (precision !== null) return precision
      if (meta.value_format === 'number') return 0

      return null
    },
    getMetaPrecision(meta) {
      if (!meta || meta.precision === null || typeof meta.precision === 'undefined') return null

      const precision = Number(meta.precision)

      if (Number.isNaN(precision)) return null

      return Math.min(Math.max(Math.round(precision), 0), 20)
    },
    formatNumber(value, precision) {
      if (precision === null) {
        return value.toLocaleString('en-US', {
          maximumFractionDigits: 20,
        })
      }

      return value.toLocaleString('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      })
    },
    getChartInfoFromDashboardInfo(chartKey) {
      const charts = Array.isArray(this.dashboardInfo.charts) ? this.dashboardInfo.charts : []

      return charts.find(item => item.chart_key === chartKey || item.key === chartKey) || null
    },
    hasChartInfo(chart) {
      return this.getInfoSections(chart.info || this.getChartInfoFromDashboardInfo(chart.chart_key)).length > 0
    },
    openChartInfo(chart) {
      const chartInfo = chart.info || this.getChartInfoFromDashboardInfo(chart.chart_key)

      this.info_modal = {
        title: chart.title || 'Chart info',
        sections: this.getInfoSections(chartInfo),
      }
      this.$modal.show('marketing-dashboard-info-modal')
    },
    closeInfoModal() {
      this.$modal.hide('marketing-dashboard-info-modal')
    },
    getInfoSections(item) {
      if (!item) return []

      const markdown = this.getInfoMarkdown(item)

      if (!markdown) return []

      return [
        {
          key: item.key || item.chart_key || 'chart-info',
          title: '',
          markdown,
        }
      ]
    },
    getInfoMarkdown(item) {
      if (!item) return ''

      if (item.value) return item.value
      if (item.markdown) return item.markdown
      if (item.body_markdown) return item.body_markdown

      return [
        item.business_markdown ? `### Business\n\n${item.business_markdown}` : '',
        item.technical_markdown ? `### Technical\n\n${item.technical_markdown}` : '',
      ].filter(Boolean).join('\n\n')
    },
    renderMarkdown(markdown) {
      const blocks = this.escapeHtml(markdown).split(/\n{2,}/)

      return blocks
        .map(block => this.renderMarkdownBlock(block))
        .join('')
    },
    renderMarkdownBlock(block) {
      const lines = block.split('\n').filter(line => line.trim())

      if (!lines.length) return ''

      const heading = lines[0].match(/^(#{1,4})\s+(.+)$/)

      if (heading && lines.length === 1) {
        const level = Math.min(heading[1].length + 2, 6)

        return `<h${level}>${this.renderInlineMarkdown(heading[2])}</h${level}>`
      }

      if (lines.every(line => /^[-*]\s+/.test(line))) {
        return `<ul>${lines.map(line => `<li>${this.renderInlineMarkdown(line.replace(/^[-*]\s+/, ''))}</li>`).join('')}</ul>`
      }

      if (lines.every(line => /^\d+\.\s+/.test(line))) {
        return `<ol>${lines.map(line => `<li>${this.renderInlineMarkdown(line.replace(/^\d+\.\s+/, ''))}</li>`).join('')}</ol>`
      }

      return `<p>${this.renderInlineMarkdown(lines.join('<br>'))}</p>`
    },
    renderInlineMarkdown(value) {
      return String(value)
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    },
  }
}
</script>

<style lang="sass" scoped>
.dashboard-chart-title
  display: inline-flex
  align-items: center
  gap: 8px

.dashboard-info-btn
  width: 28px
  height: 28px
  border-radius: 50%
  border: 1px solid #77b017
  color: #77b017
  background: #fff
  font-weight: 700
  line-height: 1
  cursor: pointer

.dashboard-info-btn--small
  width: 22px
  height: 22px
  font-size: 12px

.marketing-dashboard-info-modal__empty
  color: #777

.marketing-dashboard-info-section
  margin-bottom: 18px

.marketing-dashboard-info-section__title
  margin: 0 0 8px
  font-size: 16px

.marketing-dashboard-info-section__content
  font-size: 14px
  line-height: 1.55
  color: #333

.marketing-dashboard-info-section__content :deep(p)
  margin: 0 0 10px

.marketing-dashboard-info-section__content :deep(ul),
.marketing-dashboard-info-section__content :deep(ol)
  margin: 0 0 10px 18px

.marketing-dashboard-info-section__content :deep(code)
  padding: 1px 4px
  border-radius: 4px
  background: #f1f1f1

.chart-holder
  min-height: 100px
  position: relative
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 0 20px
  margin-top: 30px
</style>
